import React from 'react';
import { Form, InputNumber, Select, Typography } from 'antd';
import type { FormInstance } from 'antd';
import type { UnderlyingAsset } from 'state';

type Props = {
  form: FormInstance;
  assets: UnderlyingAsset[];
};

const ELIGIBLE_SPOT_TYPES = [
  'Equity',
  'Bond',
  'GovtBond',
  'CorporateBond',
  'HTMBond',
  'AFSBond',
];

/** Renders only class-19 identity fields and copies the selected spot currency to prevent mismatches. */
const SimplifiedOptionAssetFields: React.FC<Props> = ({ form, assets }) => {
  const eligibleAssets = assets.filter(asset =>
    ELIGIBLE_SPOT_TYPES.includes(asset.resourcetype),
  );

  return (
    <>
      <Form.Item
        label="PM Underlying"
        name="corresponding_spot_asset"
        rules={[{ required: true, message: 'Select an equity or bond.' }]}
      >
        <Select
          showSearch
          optionFilterProp="label"
          placeholder="Search equities and bonds"
          options={eligibleAssets.map(asset => ({
            value: asset.id,
            label: `${asset.ticker || asset.name} (${asset.resourcetype})`,
          }))}
          onChange={assetId => {
            const selected = eligibleAssets.find(asset => asset.id === assetId);
            if (selected) {
              form.setFieldsValue({
                variable_currency: selected.variable_currency,
              });
            }
          }}
        />
      </Form.Item>
      <Form.Item
        label="Option type"
        name="option_type"
        rules={[{ required: true }]}
      >
        <Select
          options={[
            { value: 0, label: 'Call' },
            { value: 1, label: 'Put' },
          ]}
        />
      </Form.Item>
      <Form.Item
        label="Issued delta"
        name="issued_delta"
        rules={[
          { required: true },
          {
            type: 'number',
            min: 0.0001,
            max: 100,
            message: 'Enter percentage points greater than 0 and at most 100.',
          },
        ]}
      >
        <InputNumber
          min={0.0001}
          max={100}
          precision={4}
          style={{ width: '100%' }}
        />
      </Form.Item>
      <Form.Item
        label="Multiplier"
        name="contract_multiplier"
        rules={[
          { required: true },
          {
            type: 'integer',
            min: 1,
            message: 'Enter a positive whole controlled-unit multiplier.',
          },
        ]}
      >
        <InputNumber min={1} precision={0} style={{ width: '100%' }} />
      </Form.Item>
      <Typography.Paragraph type="secondary">
        Settlement currency follows the selected underlying. Equity multipliers
        are shares per contract; bond multipliers are face/notional per
        contract.
      </Typography.Paragraph>
    </>
  );
};

export default SimplifiedOptionAssetFields;
